/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ScrollView, Text, View } from 'react-native';
import { styles } from './ReportsScreen.styles';
import { spacing } from '../../shared/theme/spacing';
import { ReportsMetricTile } from './components/ReportsMetricTile';
import { ReportsBlockHeading } from './components/ReportsBlockHeading';
import { ReportsDataSourceFooter } from './components/ReportsDataSourceFooter';
import { ReportsPeriodFilterPanel } from './components/ReportsPeriodFilterPanel';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { ReportsSuppliersUnifiedSection } from './components/ReportsSuppliersUnifiedSection';
import type { ReportsScreenViewModel } from './useReportsScreen';
import { orderedVsReceivedDiffs } from '../domain/orderedVsReceived';


import { formatMoney } from '../../shared/format/public-api';
export function ReportsComprasView({ vm }: { vm: ReportsScreenViewModel }) {
  const summary = vm.purchasesSummary;
  const loading = vm.purchasesLoading;
  const error = vm.purchasesError;

  if (loading && !summary) {
    return (
      <ScrollView contentContainerStyle={[styles.content, { paddingBottom: spacing.xxl }]}>
        <ReportsPeriodFilterPanel vm={vm} presetAccessibilityPrefix="Período compras" />
        <ModuleEmptyState variant="nest" loading title="Cargando informe de compras…" />
      </ScrollView>
    );
  }

  if (error && !summary) {
    return (
      <ScrollView contentContainerStyle={[styles.content, { paddingBottom: spacing.xxl }]}>
        <ReportsPeriodFilterPanel vm={vm} presetAccessibilityPrefix="Período compras" />
        <ModuleEmptyState
          variant="nest"
          kicker="Compras"
          title="No se pudo cargar el informe"
          subtitle={error}
          primaryAction={{ label: 'Reintentar', onPress: () => vm.reloadPurchases() }}
        />
      </ScrollView>
    );
  }

  if (!summary) {
    return (
      <ScrollView contentContainerStyle={[styles.content, { paddingBottom: spacing.xxl }]}>
        <ReportsPeriodFilterPanel vm={vm} presetAccessibilityPrefix="Período compras" />
        <ModuleEmptyState
          variant="nest"
          kicker="Compras"
          title="Sin datos de compras"
          subtitle="No hay recepciones en el período seleccionado."
        />
      </ScrollView>
    );
  }

  const costDiffs = orderedVsReceivedDiffs(
    summary.byProduct.map((row) => ({
      productId: row.productId,
      productName: row.productName,
      orderedUnitCost: row.orderedUnitCost ?? null,
      receivedUnitCost: row.receivedUnitCost ?? null,
    })),
  );

  return (
    <ScrollView contentContainerStyle={[styles.content, { paddingBottom: spacing.xxl }]}>
      <ReportsPeriodFilterPanel vm={vm} presetAccessibilityPrefix="Período compras" />

      <PaperSheet padded style={styles.kpiSheet} testID="informes.kpi.sheet">
        <Text style={styles.kpiSheetTitle}>Compras del período</Text>
        <View style={styles.ventasIndicatorsGrid}>
          <ReportsMetricTile
            label="Total comprado"
            value={formatMoney(summary.totalPurchased)}
            sub={`${summary.totalReceptions} recepciones`}
          />
          <ReportsMetricTile
            label="Devoluciones"
            value={formatMoney(summary.totalReturns)}
            valueTone={summary.totalReturns > 0 ? 'negative' : 'default'}
          />
          <ReportsMetricTile
            label="Neto comprado"
            value={formatMoney(summary.netPurchased)}
            sub="Comprado - devuelto"
          />
          <ReportsMetricTile
            label="Órdenes de compra"
            value={String(summary.totalOrders)}
            sub="Creadas en el período"
          />
        </View>
        {summary.totalExpenses > 0 ? (
          <View style={[styles.ventasIndicatorsGrid, { marginTop: spacing.sm }]}>
            <ReportsMetricTile
              label="Gastos de proveedor"
              value={formatMoney(summary.totalExpenses)}
              sub="Con proveedor asociado"
            />
          </View>
        ) : null}
      </PaperSheet>

      <ReportsSuppliersUnifiedSection
        bySupplier={summary.bySupplier}
        performance={vm.supplierPerformance}
        onSupplierPress={(row) =>
          vm.handleOpenSupplierInPurchases(
            row.supplierId ?? null,
            row.supplierName,
          )
        }
      />

      <ReportsBlockHeading label="Compras por producto" />
      {summary.byProduct.length === 0 ? (
        <ModuleEmptyState variant="nest" title="Sin datos" subtitle="No hay compras por producto en el período." />
      ) : (
        <PaperSheet padded>
          {summary.byProduct.slice(0, 10).map((row, i) => (
            <View key={i} style={[styles.reportsDenseListMetricsRow, i > 0 && styles.reportsDenseListRowBorder]}>
              <View style={styles.reportsDenseListTitleBlock}>
                <Text style={styles.reportsDenseListTitle}>{row.productName}</Text>
                <Text style={styles.reportsDenseListMeta}>
                  {row.quantity} unidades · costo prom. {row.avgUnitCost != null ? formatMoney(row.avgUnitCost) : '—'}
                </Text>
              </View>
              <Text style={styles.reportsDenseListMoney}>{formatMoney(row.totalCost)}</Text>
            </View>
          ))}
        </PaperSheet>
      )}

      {costDiffs.length > 0 ? (
        <>
          <ReportsBlockHeading label="Pedido vs factura" />
          <PaperSheet padded testID="reports-compras-pedido-vs-factura">
            {costDiffs.slice(0, 10).map((row, i) => (
              <View key={row.productId ?? `${row.productName}-${i}`} style={[styles.reportsDenseListMetricsRow, i > 0 && styles.reportsDenseListRowBorder]}>
                <View style={styles.reportsDenseListTitleBlock}>
                  <Text style={styles.reportsDenseListTitle}>{row.productName}</Text>
                  <Text style={styles.reportsDenseListMeta}>
                    Pedido {row.orderedUnitCost != null ? formatMoney(row.orderedUnitCost) : '—'}
                    {' · '}
                    Recibido {row.receivedUnitCost != null ? formatMoney(row.receivedUnitCost) : '—'}
                  </Text>
                </View>
              </View>
            ))}
          </PaperSheet>
        </>
      ) : null}

      <ReportsDataSourceFooter />
    </ScrollView>
  );
}
